import { apiFetch } from "../../api/client";
import type { AdminCampaignPage, AdminCampaignSummary, AdminOverview } from "../../api/types";
import { listCampaigns } from "../campaigns/api";

/**
 * `GET panel/overview` (role Consulta, design.md §8). No `client_id` is ever
 * sent — the server ignores any supplied value and resolves tenant from the
 * trusted principal — so this client never accepts or forwards one.
 */
export function getOverview(signal: AbortSignal): Promise<AdminOverview> {
  return apiFetch<AdminOverview>("/panel/overview", { signal });
}

const SCHEDULED_PAGE_SIZE = 50;
const SCHEDULED_MAX_PAGES = 10;

/**
 * Codex finding 7 (PR #382): `GET panel/campaigns` orders by
 * `fecha_programada DESC` (`AdminCampaignSqlBuilder.cs` — read-only check,
 * no sort/order query param exists on `AdminCampaignListParams`) and caps at
 * `size` (50) per page. `OverviewScreen` only ever fetched page 1, so an
 * account with more than 50 "programada" campaigns could silently miss the
 * NEAREST upcoming sends in "programadas esta semana" / "Próximos envíos" —
 * those sit on a LATER page, since DESC means page 1 holds the FURTHEST-out
 * dates.
 *
 * Walks pages sequentially, bounded at `SCHEDULED_MAX_PAGES` (10 pages / 500
 * rows): when every row fits within the bound, every page is fetched
 * forward and the result is complete. When it doesn't, only the LAST
 * `SCHEDULED_MAX_PAGES` pages are fetched — the ones holding the nearest
 * dates — so the rows most likely to matter for a dashboard are the ones
 * kept, and the furthest-out rows beyond the bound are the ones dropped.
 * `OverviewScreen` shows a short note when `items.length < total` reveals
 * this happened.
 */
/**
 * Native-review advisory n: offset-based page/size pagination drifts when
 * the underlying `programada` set changes WHILE this walk is still in
 * flight — a row inserted at the DESC-sorted front pushes every later row
 * down by one position, so the SAME campaign can land as one page's last
 * item and the next page's first item. Dedupe by `campaignId`, keeping the
 * FIRST occurrence walked (nearest-to-furthest order is preserved either
 * way since a drifted duplicate is adjacent).
 */
function dedupeByCampaignId(items: readonly AdminCampaignSummary[]): AdminCampaignSummary[] {
  const seen = new Set<string>();
  const deduped: AdminCampaignSummary[] = [];
  for (const item of items) {
    if (seen.has(item.campaignId)) continue;
    seen.add(item.campaignId);
    deduped.push(item);
  }
  return deduped;
}

/** ISO 8601 `fechaProgramada` strings compare lexicographically the same as chronologically (same fixed `Z`-suffixed shape every caller already relies on — see `derived.ts`'s own note). */
function isAtOrAfter(summary: AdminCampaignSummary, nowIso: string): boolean {
  return summary.fechaProgramada >= nowIso;
}

/**
 * Codex finding 6 (PR #382 review round 3): the simple "last
 * `SCHEDULED_MAX_PAGES` pages" window above only holds the NEAREST dates
 * when every row beyond the bound is still in the future — DESC order means
 * the LAST page holds the SMALLEST dates. `CampaignDispatchQueueFunction`'s
 * documented failure paths can leave a campaign `programada` past its own
 * date (`isWithinNextSevenDays` explicitly still counts those as relevant),
 * so once overdue rows alone exceed the bound, that same "last pages"
 * window selects the OLDEST overdue rows and can omit every genuinely
 * upcoming campaign entirely.
 *
 * Binary-searches (over page boundaries, not individual rows — `size` stays
 * `SCHEDULED_PAGE_SIZE`) for the first page whose LAST item (the page's own
 * smallest date) is already in the past, then centers the
 * `SCHEDULED_MAX_PAGES`-wide window on that boundary — half the pages
 * before it (future side) and half after (recently-overdue side), clipped
 * to `[1, lastPage]`. When every row is still future (no all-past page
 * exists), the boundary clips to `lastPage + 1` and the window degenerates
 * to exactly the previous "last pages" behavior; when every row is already
 * past, it degenerates to the FIRST pages (the least-overdue ones, since
 * DESC means page 1 still holds the largest — closest-to-now — dates).
 */
async function findPastBoundaryPage(
  lastPage: number,
  nowIso: string,
  fetchPage: (pageNumber: number) => Promise<AdminCampaignPage>,
  pageCache: Map<number, AdminCampaignPage>,
): Promise<number> {
  const cachedFetch = async (pageNumber: number): Promise<AdminCampaignPage> => {
    const cached = pageCache.get(pageNumber);
    if (cached) return cached;
    const page = await fetchPage(pageNumber);
    pageCache.set(pageNumber, page);
    return page;
  };

  let lo = 1;
  let hi = lastPage;
  let boundary = lastPage + 1; // no all-past page found yet
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const page = await cachedFetch(mid);
    const lastItem = page.items[page.items.length - 1];
    const pageEntirelyPast = lastItem !== undefined && !isAtOrAfter(lastItem, nowIso);
    if (pageEntirelyPast) {
      boundary = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return boundary;
}

/** The `SCHEDULED_MAX_PAGES`-wide `[start, end]` page range centered on `boundaryPage`, clipped to `[1, lastPage]` without shrinking below `SCHEDULED_MAX_PAGES` pages while the range fits. */
function windowAroundBoundary(boundaryPage: number, lastPage: number): { start: number; end: number } {
  const half = Math.floor(SCHEDULED_MAX_PAGES / 2);
  let start = boundaryPage - half;
  let end = boundaryPage + (SCHEDULED_MAX_PAGES - half - 1);
  if (start < 1) {
    end += 1 - start;
    start = 1;
  }
  if (end > lastPage) {
    start -= end - lastPage;
    end = lastPage;
  }
  return { start: Math.max(1, start), end: Math.min(lastPage, end) };
}

export async function getUpcomingScheduledCampaigns(nowMs: number = Date.now()): Promise<AdminCampaignPage> {
  const first = await listCampaigns({ estado: "programada", size: SCHEDULED_PAGE_SIZE, page: 1 });
  // Native-review advisory n: `total` is updated from the LATEST page
  // actually walked (not just this page-1 discovery read) so a shifting
  // account's reported total — and therefore OverviewScreen's own
  // `items.length < total` truncation note — stays consistent with the rows
  // this walk actually returns, instead of a stale snapshot from before the
  // walk even started.
  let total = first.total;
  const lastPage = Math.max(1, Math.ceil(total / SCHEDULED_PAGE_SIZE));

  if (lastPage <= 1) {
    return first;
  }

  const fetchPage = (pageNumber: number) =>
    listCampaigns({ estado: "programada", size: SCHEDULED_PAGE_SIZE, page: pageNumber });

  let items: AdminCampaignSummary[];
  if (lastPage <= SCHEDULED_MAX_PAGES) {
    items = first.items;
    // Codex finding 8 (PR #382 review round 3): `lastPage` above was
    // computed ONCE from the discovery read and never revisited, so an
    // insertion that grows `total` WHILE this loop is still walking (e.g.
    // page 1's report of 100 rows growing to 101 by the time page 2
    // answers) left the newly-shifted row on a page this loop never
    // fetched — silently dropped instead of merely duplicated. Recomputing
    // the bound from each page's own `total` lets the walk extend to cover
    // it; capped well above the normal case so a pathological burst of
    // concurrent inserts still terminates instead of walking indefinitely.
    const SAFETY_MAX_PAGE = SCHEDULED_MAX_PAGES * 3;
    // Codex finding 3 (PR #382 round 4): a `programada` row can leave the
    // filtered set WHILE this forward walk is still in flight (e.g. a
    // dispatch timer claims it) — every later row then shifts toward the
    // preceding, already-read page, so simply continuing from here would
    // keep a page that no longer reflects the current set while never
    // revisiting the page the shifted row landed on; the latest `total`
    // would still match the (wrong) item count, so neither dedup nor the
    // truncation check could ever catch it. A later page's own `total`
    // coming back LOWER than the total this walk started with is the
    // signal — restart from page 1 against the fresh total instead of
    // reconciling positions by hand. Bounded so a pathological account
    // churning rows on every single page still terminates.
    const MAX_RESTARTS = 3;
    let restarts = 0;
    let dynamicLastPage = lastPage;
    let pageNumber = 2;
    while (pageNumber <= dynamicLastPage) {
      const next = await fetchPage(pageNumber);
      if (next.total < total && restarts < MAX_RESTARTS) {
        restarts += 1;
        const restarted = await fetchPage(1);
        total = restarted.total;
        items = restarted.items;
        dynamicLastPage = Math.max(1, Math.ceil(total / SCHEDULED_PAGE_SIZE));
        pageNumber = 2;
        continue;
      }
      items = items.concat(next.items);
      total = next.total;
      dynamicLastPage = Math.min(
        Math.max(dynamicLastPage, Math.ceil(total / SCHEDULED_PAGE_SIZE)),
        SAFETY_MAX_PAGE,
      );
      pageNumber += 1;
    }
  } else {
    const nowIso = new Date(nowMs).toISOString();
    // Codex finding (PR #382 round 5): `lastPage` (and the boundary/window
    // derived from it) was computed ONCE from the discovery read and never
    // revisited for the rest of this branch — a response revealing the total
    // grew (or shrank) WHILE the boundary search or window fetch was still
    // in flight left the window clamped to the stale bound. E.g. page 1
    // reporting exactly 550 rows (lastPage=11, window [2,11]) followed by a
    // later response revealing an insertion grew the total to 551
    // (lastPage=12) left the row that shifted onto the new page 12 forever
    // unfetched. Restart the boundary search and window fetch from a fresh
    // discovery read whenever a page reports a total different from the one
    // the current attempt's window was computed from — bounded, like the
    // sibling branch above, so a pathological account churning its total on
    // every page still terminates.
    const MAX_LARGE_RESTARTS = 3;
    let restarts = 0;
    let dynamicFirst = first;
    let dynamicLastPage = lastPage;

    for (;;) {
      const expectedTotal = dynamicFirst.total;
      // Once restarts are exhausted, stop aborting on drift and accept the
      // best-effort result instead of returning nothing — same bounded
      // "give up gracefully" policy the sibling branch above applies.
      const canRestartAgain = restarts < MAX_LARGE_RESTARTS;
      const pageCache = new Map<number, AdminCampaignPage>([[1, dynamicFirst]]);
      let totalDrifted = false;
      let latestTotal = expectedTotal;
      const watchedFetch = async (pageNumber: number): Promise<AdminCampaignPage> => {
        const page = await fetchPage(pageNumber);
        latestTotal = page.total;
        if (canRestartAgain && page.total !== expectedTotal) {
          totalDrifted = true;
        }
        return page;
      };

      const boundaryPage = await findPastBoundaryPage(dynamicLastPage, nowIso, watchedFetch, pageCache);
      let windowItems: AdminCampaignSummary[] = [];
      if (!totalDrifted) {
        const { start, end } = windowAroundBoundary(boundaryPage, dynamicLastPage);
        for (let pageNumber = start; pageNumber <= end && !totalDrifted; pageNumber += 1) {
          const cached = pageCache.get(pageNumber);
          const next = cached ?? (await watchedFetch(pageNumber));
          if (totalDrifted) break;
          windowItems = windowItems.concat(next.items);
        }
      }

      if (totalDrifted) {
        restarts += 1;
        dynamicFirst = await fetchPage(1);
        dynamicLastPage = Math.max(1, Math.ceil(dynamicFirst.total / SCHEDULED_PAGE_SIZE));
        continue;
      }

      items = windowItems;
      total = latestTotal;
      break;
    }
  }

  const deduped = dedupeByCampaignId(items);
  return { items: deduped, page: 1, size: deduped.length, total };
}
